/* eslint-disable @typescript-eslint/ban-ts-ignore */
import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router';
import { CircularProgress } from '@material-ui/core/';
import { RouteComponentProps } from 'react-router-dom';
import styled from 'styled-components/macro';
import FileSaver from 'file-saver';
import domtoimage from 'dom-to-image';
import {
  BackTo,
  showToast,
  Typography,
  Charts,
  Button,
} from '@deeptrue-frontend/ui';
import { OrganizationType } from 'store/modules/auth/auth';
import { Dashboard } from 'components/Layout/';
import { useFormatMessage } from 'components/IntlProvider';
import Legend from './Legend';
import ProjectMenu from '../../Common/ProjectMenu';
import { WithProjectProps } from '../../withProject';
import StoreProvider from 'components/StoreProvider/StoreProvider';
import api from 'api';
import {
  ReportsType,
  SubgroupType,
  ChartType,
  SectionType,
  ChartDetailsType,
} from './types';
import BrandFunnel from './BrandFunnel/BrandFunnel';

const { connect, selectors } = StoreProvider;

interface ProjectProps
  extends RouteComponentProps<{ id: string; codename: string }> {
  product: WithProjectProps['product'];
  project: WithProjectProps['project'];
  organization: OrganizationType;
  isAdmin: boolean;
  isBexioUser: boolean;
}

const DownloadChart = styled(Button)`
  position: absolute;
  top: -4rem;
  right: 0;
`;

const LoaderWrapper = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 5rem;
  align-items: center;
  width: 100%;
  height: 100%;
`;

const Container = styled.div`
  display: flex;
  width: 100%;
  flex-direction: row;

  & > div:first-of-type {
    position: relative;
    padding-right: 2rem;
    margin-bottom: 4rem;
    border-right: 1px solid ${({ theme }) => theme.colors.quillGray};
    width: 30rem;
  }
`;

const TabTitle = styled(Typography)`
  font-size: 1.25rem;
  width: 100%;
`;

const ProjectMenuWrapper = styled.div`
  width: 100%;
  .tab-bar {
    margin-left: -2rem;
    padding-left: 2rem;
    margin-right: -2rem;
    padding-right: 2rem;
  }
`;

const ReportContainer = styled.div`
  width: 100%;
  padding: 0 1rem;
`;

const Ol = styled.ol<{
  listType: 'lower-latin' | 'lower-roman' | 'none';
  padding?: string;
  border?: boolean;
  margin?: string;
}>`
  list-style-type: ${({ listType }) => listType};
  padding: ${({ padding }) => (padding ? padding : '0')};
  display: flex;
  flex-direction: column;

  li:first-of-type {
    margin-left: 0;
  }
  li:last-of-type {
    margin-right: 0;
  }

  border-bottom: ${({ border }) => (border ? '1px solid #E6E6E5' : 'none')};
  margin-bottom: 1rem;
  padding-bottom: 1rem;
  width: 100%;
  ${({ margin }) =>
    margin &&
    `
    margin: ${margin};
  `}
`;

const Li = styled.li`
  margin-right: 1rem;
  display: flex;
  justify-content: center;
  margin: 2rem;
  position: relative;
`;

export enum Tabs {
  'markenstärke' = 0,
  'markenimage',
  'brand_funnel',
}

const defineChartType = (chartDetails: ChartDetailsType) => {
  if (!chartDetails) return;

  const {
    type,
    data,
    labels,
    layout,
    legend,
    name,
    unit,
    x_low,
    x_high,
    x_median,
    y_high,
    y_low,
    y_median,
    max_chart_value,
    min_chart_value,
  } = chartDetails;

  if (type === 'bar') {
    return (
      <Charts.BarChart
        data={data}
        labels={labels}
        layout={layout}
        unit={unit || undefined}
        domain={[min_chart_value, max_chart_value]}
      />
    );
  } else if (type === 'bar_multi') {
    return (
      <Charts.BarChartMulti
        domain={[min_chart_value, max_chart_value]}
        data={data}
        labels={labels}
        layout={layout}
        unit={unit || undefined}
        // @ts-ignore
        legend={legend}
        maxChartValue={max_chart_value}
        minChartValue={min_chart_value}
      />
    );
  } else if (type === 'scatterplot') {
    return (
      <Charts.Scatterplot
        data={{
          data,
          x_low,
          x_high,
          x_median,
          y_high,
          y_low,
          y_median,
        }}
        width={400}
        height={400}
        medianAxes={true}
        // @ts-ignore
        chartName={`id-${name}`}
      />
    );
  } else if (type === 'brand_funnel') {
    return <BrandFunnel data={data} legend={legend} />;
  } else if (type === 'scatterplot_multi_year') {
    const filteredData = data.map((singleData: any) => {
      return {
        ...singleData,
        history: singleData.history.filter((singleHistory: any) => {
          const isEmpty = Object.keys(singleHistory).length === 0;

          return !isEmpty;
        }),
      };
    });

    return (
      <Charts.ScatterPlotMulti
        data={{
          data: filteredData,
          x_low,
          x_high,
          x_median,
          y_high,
          y_low,
          y_median,
        }}
        medianAxes={true}
        width={400}
        height={400}
        chartName={`multi-${name}`}
      />
    );
  }

  return null;
};

const BranValuatorReport = ({
  location,

  match: { params },
  project,
  organization,
}: any) => {
  const [isLoading, setIsLoading] = useState(true);
  const [selectedTab, changeTab] = useState(Tabs.markenstärke);
  const [report, setReport] = useState<ReportsType | null>(null);
  const [selectedSection, setSection] = useState<SectionType | null>();
  const history = useHistory();
  const choosenTab = Tabs[selectedTab];

  // @ts-ignore
  const dataToDisplay = report ? report[choosenTab] : [];

  const t = useFormatMessage();

  const { id: projectId } = project;

  useEffect(() => {
    if (project.id !== -1) {
      api.resources
        .getBrandValuatorReport({
          organizationCodename: organization,
          projectId: projectId,
        })
        .then(({ data }) => {
          const { charts_data } = data;
          setReport(charts_data);
          setIsLoading(false);
        })
        .catch(() => {
          setIsLoading(false);
          showToast({
            type: 'error',
            title: t(
              'errors.toast.general',
              'Something went wrong, please contact administrator'
            ),
          });
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [project.id]);

  useEffect(() => {
    const section = dataToDisplay[0];
    if (section) {
      setSection({
        ...dataToDisplay[0],
        isWholeGroupSelected: true,
        group_name: dataToDisplay[0].group_name,
        selected_group_name: `${dataToDisplay[0].group_name}-0`,
      });
    }
  }, [dataToDisplay]);

  // INFO left as most of functionality is done. But for now we will be using only PNG format.
  // Chart Legend is not rendered as SVG element, we need to find a way to convert it to svg

  // const saveChartAsSvg = async (e: any, name: string | null) => {
  //   const imageContainer = e.target.parentNode;

  //   const svg = imageContainer.querySelector('svg');
  //   svg.style.fontFamily = 'Inter, Helvetica';

  //   const serializer = new XMLSerializer();
  //   let source = serializer.serializeToString(svg);

  //   //add name spaces.
  //   if (!source.match(/^<svg[^>]+xmlns="http\:\/\/www\.w3\.org\/2000\/svg"/)) {
  //     source = source.replace(
  //       /^<svg/,
  //       '<svg xmlns="http://www.w3.org/2000/svg"'
  //     );
  //   }
  //   if (!source.match(/^<svg[^>]+"http\:\/\/www\.w3\.org\/1999\/xlink"/)) {
  //     source = source.replace(
  //       /^<svg/,
  //       '<svg xmlns:xlink="http://www.w3.org/1999/xlink"'
  //     );
  //   }

  //   //add xml declaration
  //   source = '<?xml version="1.0" standalone="no"?>\r\n' + source;

  //   //convert svg source to URI data scheme.
  //   const url =
  //     'data:image/svg+xml;charset=utf-8,' + encodeURIComponent(source);

  //   const a = document.createElement('a');
  //   // const e = new MouseEvent('click');
  //   a.download = `${name}.svg`;
  //   a.href = url;
  //   a.click();
  // };

  const saveChartAsPNG = async (e: any, name: string | null) => {
    const imageContainer = e.target.parentNode;
    const chartSvg = imageContainer.querySelector('div');

    await domtoimage
      .toPng(chartSvg, {
        bgcolor: 'transparent',
      })

      .then(function (blob) {
        FileSaver.saveAs(blob, `${name || 'chart'}.png`);
      });
  };

  const overviewUrl = params.codename
    ? `/admin/organizations/${params.codename}/projects/`
    : `/`;

  return (
    <Dashboard
      title={project.name || t('page.project', 'Project details')}
      location={location}
      isFullWidth
      combinedTitle={project.combinedTitle}
    >
      <BackTo onClick={() => history.push(overviewUrl)}>
        {t('project.backTo.overview', 'Back to overview')}
      </BackTo>
      <ProjectMenuWrapper>
        <ProjectMenu
          id={params.id}
          projectStatus={project.status}
          isProjectLoaded={project.id !== -1}
        />
      </ProjectMenuWrapper>

      {isLoading ? (
        <LoaderWrapper>
          <CircularProgress size={80} color="primary" />
        </LoaderWrapper>
      ) : (
        <Container>
          <Legend
            changeTab={changeTab}
            selectedTab={selectedTab}
            setSection={setSection}
            selectedSection={selectedSection}
            reportsLegend={[...dataToDisplay]}
            tabs={[
              {
                value: Tabs.markenstärke,
                name: Tabs[Tabs.markenstärke],
              },
              {
                value: Tabs.markenimage,
                name: Tabs[Tabs.markenimage],
              },

              {
                value: Tabs.brand_funnel,
                name: 'Brand funnel',
              },
            ]}
          />

          <ReportContainer className="report-brand-valuator">
            <Ol listType="lower-latin">
              {selectedSection && (
                <>
                  <TabTitle variant="copy1" mt="1.5rem">
                    {selectedSection.group_name}
                  </TabTitle>

                  <Ol listType="none" padding="0" margin="1rem 0">
                    {selectedSection.name && (
                      <Typography variant="copy1">
                        {selectedSection.name}
                      </Typography>
                    )}
                    {selectedSection.subgroups
                      ? [...selectedSection.subgroups].map(
                          (subgroup: SubgroupType, index: number) => {
                            const { charts, name } = subgroup;

                            return (
                              <Ol listType="none" key={index} border>
                                <Typography>{name}</Typography>

                                {charts.map(
                                  (chart: ChartType, index: number) => {
                                    const chartToRender = defineChartType(
                                      chart.data
                                    );

                                    return (
                                      <Li key={`${index}`}>
                                        <DownloadChart
                                          variant="outlined"
                                          onClick={(e: any) =>
                                            saveChartAsPNG(e, name)
                                          }
                                        >
                                          Download
                                        </DownloadChart>
                                        {/* <DownloadChart
                                          onClick={(e: any) =>
                                            saveChartAsPNG(e, name)
                                          }
                                        >
                                          Download PNG
                                        </DownloadChart> */}
                                        {chartToRender}
                                      </Li>
                                    );
                                  }
                                )}
                              </Ol>
                            );
                          }
                        )
                      : selectedSection.charts.map(
                          (chart: ChartType, index: number) => {
                            const chartToRender = defineChartType(chart.data);

                            const nameToRender =
                              selectedSection && selectedSection.name
                                ? selectedSection.name
                                : selectedSection.group_name;
                            return (
                              <Li key={index}>
                                <DownloadChart
                                  onClick={(e: any) =>
                                    saveChartAsPNG(e, nameToRender)
                                  }
                                >
                                  Download
                                </DownloadChart>
                                {/* <DownloadChart
                                  onClick={(e: any) =>
                                    saveChartAsPNG(e, nameToRender)
                                  }
                                >
                                  Download PNG
                                </DownloadChart> */}
                                {chartToRender}
                              </Li>
                            );
                          }
                        )}
                  </Ol>
                </>
              )}
            </Ol>
          </ReportContainer>
        </Container>
      )}
    </Dashboard>
  );
};

export default connect((state) => ({
  isAdmin: selectors.authSelectors.isDeepTrueAdmin(state),
}))(BranValuatorReport);
